// Global Cart API
// Plain ESM module that wraps existing cart-functions utilities with a global-friendly API.
// External callers (Replo pages, marketing scripts, inline onclick handlers) use
// window.pvolve.cart.add() / .checkout() to add products to cart.

import {
	addItemsToCart,
	getCart,
	getDOMCart,
	getDOMCartSectionApiIds,
	hasMatchingBundleInCart,
	isMembershipCartItem,
	openCartDrawerWithOptimisticItem,
	renderResponseToCartDrawer,
	type AddToCartItem,
} from '@/scripts/core/cart-functions'
import type { Cart, CartErrorResponse } from '@/scripts/shopify'
import { publish, PUB_SUB_EVENTS } from '@/scripts/core/global'
import {
	syncPayLaterCartAttribute,
	syncInternalAttribFromCookie,
} from '@/scripts/core/cart-attribution'
import {
	resolveProductInput,
	fetchProductById,
	getFirstAvailableVariant,
	type ShopifyProductJSON,
} from '@/scripts/core/product-resolver'
import {
	getCachedProductById,
	getCachedProductByVariantId,
	refreshCartSnapshot,
} from '@/scripts/core/product-cache'
import { buildOptimisticCartItems } from '@/scripts/cart/optimistic-cart'
import { CartDrawer } from '@/scripts/cart/cart-drawer'
import { forceReleaseAllLocks, isLocked } from '@/scripts/cart/optimistic-lock'
import type { LockToken } from '@/scripts/cart/optimistic-lock'
import { cartDebug, summarizeCartItems } from '@/scripts/cart/optimistic-debug'
import {
	getCartState,
	isCartStateInitialized,
} from '@/scripts/cart/cart-state'

export type CartApiOptions = {
	quantity?: number
	variantId?: number
	sellingPlanId?: number
	properties?: Record<string, string>
	openCart?: boolean
	goToCheckout?: boolean
	payLater?: string | null
	purchaseAsGift?: boolean
	/** When true, treat numeric input as a product ID (not variant ID) and resolve to its first available variant */
	isProductId?: boolean
}

export type CartApiResult = {
	success: boolean
	error?: string
}

let isProcessing = false
let lastProcessTime = 0
let isBackForwardRevalidationInFlight = false
let hasInitializedBackForwardCartRevalidation = false
const DEBOUNCE_MS = 500
const NUMERIC_ID_PATTERN = /^\d+$/

function isBackForwardRestore(event: PageTransitionEvent): boolean {
	if (event.persisted) return true

	const navigationEntry = window.performance.getEntriesByType(
		'navigation'
	)[0] as PerformanceNavigationTiming | undefined

	return navigationEntry?.type === 'back_forward'
}

async function revalidateCartAfterHistoryRestore(): Promise<void> {
	if (isBackForwardRevalidationInFlight) return

	isBackForwardRevalidationInFlight = true
	cartDebug('cart-api:history-restore:revalidate:start')

	try {
		isProcessing = false
		forceReleaseAllLocks()
		refreshCartSnapshot()

		const cartEl = getDOMCart()
		if (cartEl instanceof CartDrawer) {
			await cartEl.forceRefreshFromServer()
		}

		cartDebug('cart-api:history-restore:revalidate:done', {
			hasCartDrawer: cartEl instanceof CartDrawer,
		})
	} catch (e) {
		cartDebug('cart-api:history-restore:revalidate:error', {
			error: e instanceof Error ? e.message : String(e),
		})
	} finally {
		isBackForwardRevalidationInFlight = false
	}
}

function initBackForwardCartRevalidation(): void {
	if (hasInitializedBackForwardCartRevalidation) return

	hasInitializedBackForwardCartRevalidation = true
	window.addEventListener('pageshow', (event) => {
		if (!isBackForwardRestore(event)) return
		void revalidateCartAfterHistoryRestore()
	})
}

function getProductForOptimistic(
	variantId: number
): ShopifyProductJSON | null {
	return getCachedProductByVariantId(variantId) ?? null
}

/** Synchronous cache-only lookup by product ID. Used by the pre-resolve optimistic
 *  path when the caller passed `isProductId: true` — lets us render a real row
 *  before the async `fetchProductById` variant-resolution call runs. */
function getProductForOptimisticByProductId(
	productId: number
): ShopifyProductJSON | null {
	return getCachedProductById(productId) ?? null
}

/** Synchronous duplicate-bundle check against client-side cart state.
 *  Mirrors hasMatchingBundleInCart (`cart-functions.ts:109-122`) but reads from
 *  cart-state instead of requiring an async /cart.js fetch. Used to short-circuit
 *  cartApiAdd before it fires the optimistic render — avoids the "forever loading"
 *  bug where a duplicate click injects a row, acquires the lock, and then bails in
 *  the duplicate-skip branch without releasing anything.
 *
 *  Returns null when cart-state isn't initialized yet (caller falls back to the
 *  async /cart.js check further down the pipeline). */
function hasMatchingBundleInCartStateSync(
	variantId: number,
	payLater: string | null
): boolean | null {
	if (!isCartStateInitialized()) return null
	const normalizedPayLater = (payLater ?? '').toLowerCase() || null
	return getCartState().items.some((item) => {
		if (item.variantId !== variantId) return false
		if (!item.isBundle) return false
		const itemPayLater =
			(item.properties?._pay_later ?? '').toLowerCase() || null
		return itemPayLater === normalizedPayLater
	})
}

function isExclusiveProduct(product: ShopifyProductJSON | null): boolean {
	if (!product) return false
	const title = (product.title ?? '').toLowerCase()
	const handle = (product.handle ?? '').toLowerCase()
	const productType = (product.type ?? '').toLowerCase()

	return (
		title.includes('bundle') ||
		handle.includes('bundle') ||
		productType === 'bundle' ||
		title.includes('membership') ||
		productType === 'streaming' ||
		productType.includes('membership')
	)
}

function normalizeCartApiInput(input: number | string): number | string {
	if (typeof input === 'number') return input
	const trimmed = input.trim()
	if (!NUMERIC_ID_PATTERN.test(trimmed)) return input
	const parsed = Number(trimmed)
	return Number.isSafeInteger(parsed) ? parsed : input
}

export async function cartApiAdd(
	input: number | string,
	options: CartApiOptions = {}
): Promise<CartApiResult> {
	const normalizedInput = normalizeCartApiInput(input)
	cartDebug('cart-api:add:start', {
		input,
		normalizedInput,
		options,
	})
	const now = Date.now()
	if (isProcessing || now - lastProcessTime < DEBOUNCE_MS) {
		return { success: false, error: 'busy' }
	}

	isProcessing = true
	lastProcessTime = now

	try {
		const wantsOptimistic =
			!options.goToCheckout && options.openCart !== false
		let optimisticStarted = false
		let shouldResolveExclusiveConflicts = false
		const isTrialAdd =
			String(options.properties?._is_trial ?? '').toLowerCase() === 'true'
		let lockToken: LockToken | null = null
		let cartDrawer: CartDrawer | null = null

		// ── 0. Early sync duplicate-bundle short-circuit ────────────────
		// Before firing any optimistic render or network call, check if the
		// bundle is already in cart with the same pay-later state. If so:
		// - No optimistic row / no lock / no /cart/add.js / no duplication.
		// - For goToCheckout (PayPal/AfterPay express), still redirect.
		// - For regular adds, just open the drawer.
		// Falls through to the async check in Step 4 if cart-state isn't
		// initialized yet (first visits before /cart.js has hydrated it).
		if (
			typeof normalizedInput === 'number' &&
			!options.isProductId &&
			!options.variantId
		) {
			const syncDuplicate = hasMatchingBundleInCartStateSync(
				normalizedInput,
				options.payLater ?? null
			)
			if (syncDuplicate === true) {
				cartDebug('cart-api:add:sync-duplicate-skip', {
					variantId: normalizedInput,
					payLater: options.payLater ?? null,
					goToCheckout: options.goToCheckout ?? false,
				})
				if (options.goToCheckout) {
					window.location.href = '/checkout'
				} else if (options.openCart !== false) {
					const cartEl = getDOMCart()
					if (
						cartEl instanceof CartDrawer &&
						!cartEl.classList.contains('active')
					) {
						cartEl.open()
					}
				}
				isProcessing = false
				return { success: true }
			}
		}

		// ── 1. Pre-resolution optimistic attempt (numeric variant/product IDs) ──
		// Works for both variant-ID input and product-ID input (isProductId=true).
		// For product-ID input we look up the cached product directly and pick its
		// first available variant, so the drawer can render a real row *before* the
		// async fetchProductById variant-resolution call runs in Step 2.
		if (
			wantsOptimistic &&
			typeof normalizedInput === 'number'
		) {
			const cachedProduct = options.isProductId
				? getProductForOptimisticByProductId(normalizedInput)
				: getProductForOptimistic(normalizedInput)
			const renderVariantId = options.isProductId
				? (options.variantId ??
					(cachedProduct
						? getFirstAvailableVariant(cachedProduct)?.id
						: undefined) ??
					normalizedInput)
				: (options.variantId ?? normalizedInput)
			shouldResolveExclusiveConflicts =
				isTrialAdd || isExclusiveProduct(cachedProduct)
			try {
				const optimisticData = buildOptimisticCartItems({
					variantId: renderVariantId,
					product: cachedProduct ?? undefined,
					quantity: options.quantity ?? 1,
					payLater: options.payLater,
					purchaseAsGift: options.purchaseAsGift,
					isTrial:
						String(
							options.properties?._is_trial ?? ''
						).toLowerCase() === 'true',
				})
				const hasExclusiveAssociated =
					optimisticData.associated.some(
						(item) =>
							item.type === 'bundle_membership' ||
							item.type === 'alcm'
					)
				shouldResolveExclusiveConflicts =
					optimisticData.main.isExclusive ||
					hasExclusiveAssociated ||
					isTrialAdd
				cartDebug('cart-api:add:pre-resolve-optimistic', {
					mainVariantId: optimisticData.main.variantId,
					mainIsExclusive: optimisticData.main.isExclusive,
					mainIsSkeleton: optimisticData.main.isSkeleton ?? false,
					hasExclusiveAssociated,
					shouldResolveExclusiveConflicts,
					associated: optimisticData.associated.map((a) => ({
						type: a.type,
						variantId: a.variantId,
						title: a.title,
					})),
				})
				openCartDrawerWithOptimisticItem(
					optimisticData.main,
					optimisticData.associated
				)
				const cartEl = getDOMCart()
				if (cartEl instanceof CartDrawer) {
					cartDrawer = cartEl
					lockToken =
						cartDrawer.acquireOptimisticLock('cart-api-add')
					optimisticStarted = true
				}
			} catch (e) {
				cartDebug('cart-api:add:pre-resolve-optimistic-error', {
					error: e instanceof Error ? e.message : String(e),
				})
				console.warn(
					'[CartAPI] Optimistic pre-resolve path failed:',
					e
				)
			}
		}

		// ── 2. Resolve variant ID ───────────────────────────────────
		let variantId: number

		if (options.isProductId && typeof normalizedInput === 'number') {
			const product = await fetchProductById(normalizedInput)
			if (!product) {
				if (optimisticStarted) forceReleaseAllLocks()
				return {
					success: false,
					error: `Product ${normalizedInput} not found`,
				}
			}
			const variant = getFirstAvailableVariant(product)
			if (!variant) {
				if (optimisticStarted) forceReleaseAllLocks()
				return {
					success: false,
					error: `No available variant for product ${normalizedInput}`,
				}
			}
			variantId = options.variantId ?? variant.id
		} else if (typeof normalizedInput === 'number') {
			// Pre-send sanity check: callers sometimes pass a product ID in the
			// variant slot (no `isProductId: true` flag). Shopify's /cart/add.js
			// rejects product IDs with a 422, which we then catch and retry as a
			// product ID — wasting ~1.5s and one extra /cart/add.js request per
			// click. If the collection seed has the product cached by product ID
			// but not by variant ID, auto-rewrite to the first available variant
			// before the network call. Respects `options.variantId` override.
			const cachedByVariant = getCachedProductByVariantId(normalizedInput)
			const cachedByProduct = getCachedProductById(normalizedInput)
			if (!cachedByVariant && cachedByProduct && !options.variantId) {
				const fallbackVariant = getFirstAvailableVariant(cachedByProduct)
				if (fallbackVariant) {
					cartDebug('cart-api:add:auto-rewrite-product-id-to-variant', {
						originalInput: normalizedInput,
						resolvedVariantId: fallbackVariant.id,
						productId: cachedByProduct.id,
						productHandle: cachedByProduct.handle,
					})
					variantId = fallbackVariant.id
				} else {
					variantId = normalizedInput
				}
			} else {
				variantId = options.variantId ?? normalizedInput
			}
		} else {
			const resolved = await resolveProductInput(normalizedInput)
			variantId = options.variantId ?? resolved.variant.id
		}

		// ── 2b. Post-resolution optimistic attempt (handle/productId inputs) ──
		if (wantsOptimistic && !optimisticStarted) {
			const product = getProductForOptimistic(variantId)
			shouldResolveExclusiveConflicts =
				shouldResolveExclusiveConflicts ||
				isTrialAdd ||
				isExclusiveProduct(product)
			try {
				const optimisticData = buildOptimisticCartItems({
					variantId,
					product: product ?? undefined,
					quantity: options.quantity ?? 1,
					payLater: options.payLater,
					purchaseAsGift: options.purchaseAsGift,
					isTrial:
						String(
							options.properties?._is_trial ?? ''
						).toLowerCase() === 'true',
				})
				const hasExclusiveAssociated =
					optimisticData.associated.some(
						(item) =>
							item.type === 'bundle_membership' ||
							item.type === 'alcm'
					)
				shouldResolveExclusiveConflicts =
					optimisticData.main.isExclusive ||
					hasExclusiveAssociated ||
					isTrialAdd
				cartDebug('cart-api:add:post-resolve-optimistic', {
					variantId,
					mainIsExclusive: optimisticData.main.isExclusive,
					mainIsSkeleton: optimisticData.main.isSkeleton ?? false,
					hasExclusiveAssociated,
					shouldResolveExclusiveConflicts,
					associated: optimisticData.associated.map((a) => ({
						type: a.type,
						variantId: a.variantId,
						title: a.title,
					})),
				})
				openCartDrawerWithOptimisticItem(
					optimisticData.main,
					optimisticData.associated
				)
				const cartEl = getDOMCart()
				if (cartEl instanceof CartDrawer) {
					cartDrawer = cartEl
					lockToken =
						cartDrawer.acquireOptimisticLock('cart-api-add')
					optimisticStarted = true
				}
			} catch (e) {
				cartDebug('cart-api:add:post-resolve-optimistic-error', {
					error: e instanceof Error ? e.message : String(e),
				})
				console.warn(
					'[CartAPI] Optimistic post-resolve path failed:',
					e
				)
			}
		}

		// ── 3. Build cart item ──────────────────────────────────────
		const properties: Record<string, string> = {
			...(options.properties ?? {}),
		}
		if (options.purchaseAsGift) {
			properties['_purchase_as_gift'] = 'true'
		}
		if (options.payLater) {
			properties['_pay_later'] = options.payLater
		}

		const item: AddToCartItem = {
			id: variantId,
			quantity: options.quantity ?? 1,
			properties,
		}
		if (options.sellingPlanId) {
			item.selling_plan = options.sellingPlanId
		}

		// ── 4. Pre-add cleanup + cart add ────────────────────────────
		const sections = getDOMCartSectionApiIds()
		const needsCleanup = !!options.payLater
		let cart: Awaited<ReturnType<typeof addItemsToCart>>

		// Check if the same bundle is already in cart with matching payLater state
		const currentCart = await getCart()
		const payLaterToCompare = options.payLater || null
		const isSameBundleInCart =
			'status' in currentCart
				? false
				: hasMatchingBundleInCart(
						currentCart.items,
						variantId,
						payLaterToCompare
					)

		cartDebug('cart-api:duplicate-check', {
			variantId,
			payLaterToCompare,
			isSameBundleInCart,
		})

		// Run pay-later cleanup BEFORE the duplicate check so stale
		// memberships/GWPs are removed even when we skip the add.
		if (needsCleanup) {
			cartDebug('cart-api:add:pre-cleanup', {
				variantId,
				payLater: options.payLater ?? null,
				isSameBundleInCart,
			})
			await preAddCartCleanup(
				variantId,
				options.payLater ?? null,
				currentCart
			)
		}

		// Skip the add entirely when the exact bundle + pay-later state is
		// already in cart. Shopify's /cart/add.js would otherwise increment
		// the quantity (or create a duplicate line), producing the
		// "click pay-later twice → 2 bundles" bug. Cleanup above still ran,
		// so any stale memberships/GWPs are gone.
		if (isSameBundleInCart) {
			cartDebug('cart-api:add:same-bundle-skip', {
				variantId,
				optimisticStarted,
				needsCleanup,
				goToCheckout: options.goToCheckout ?? false,
			})

			// Reconcile drawer state. If the pre-resolve block injected an
			// optimistic row and set line prices to loading, OR if cleanup
			// just modified cart state via /cart/update.js, we need fresh
			// sections — otherwise the lock stays held, optimistic row
			// stays in the DOM, and/or post-cleanup state isn't rendered.
			// forceRefreshFromServer releases locks, fetches sections, and
			// atomically re-renders.
			if (optimisticStarted && cartDrawer) {
				void cartDrawer.forceRefreshFromServer()
			} else if (needsCleanup) {
				const cartEl = getDOMCart()
				if (cartEl instanceof CartDrawer) {
					void cartEl.forceRefreshFromServer()
				}
			}

			if (options.goToCheckout) {
				window.location.href = '/checkout'
			} else if (options.openCart !== false) {
				const cartEl = getDOMCart()
				if (
					cartEl instanceof CartDrawer &&
					!cartEl.classList.contains('active')
				) {
					cartEl.open()
				}
			}
			isProcessing = false
			return { success: true }
		}

		cart = await addItemsToCart(
			[item],
			sections,
			optimisticStarted
				? {
						conflictMode: shouldResolveExclusiveConflicts
							? 'exclusive_only'
							: 'none',
					}
				: undefined
		)

		// Shopify cart endpoints are session-locked. If a parallel /cart/update.js
		// request wins the lock (apps/pixels), /cart/add can return 409. Retry once
		// after a short delay to let the competing request finish.
		if ('status' in cart && cart.status === 409) {
			cartDebug('cart-api:add:retry-on-409', {
				variantId,
			})
			await new Promise((resolve) => setTimeout(resolve, 450))
			cart = await addItemsToCart(
				[item],
				sections,
				optimisticStarted
					? {
							conflictMode: shouldResolveExclusiveConflicts
								? 'exclusive_only'
								: 'none',
						}
					: undefined
			)
		}

		cartDebug('cart-api:add:addItemsToCart-response', {
			hasError: 'status' in cart,
			hasSections: 'sections' in cart && Boolean(cart.sections),
			itemCount: 'items' in cart ? cart.items.length : undefined,
			items: 'items' in cart ? summarizeCartItems(cart.items) : undefined,
		})

		// If failed and input was a raw number, retry as product ID lookup
		if (
			'status' in cart &&
			typeof normalizedInput === 'number' &&
			!options.variantId &&
			!options.isProductId
		) {
			console.warn(
				`[CartAPI] Variant ${normalizedInput} failed, retrying as product ID lookup`
			)
			if (optimisticStarted) {
				forceReleaseAllLocks()
				optimisticStarted = false
				lockToken = null
				cartDrawer = null
			}
			window.__skipGwpAdditions = false
			const retryProduct = await fetchProductById(normalizedInput)
			if (retryProduct) {
				const variant = getFirstAvailableVariant(retryProduct)
				if (variant) {
					item.id = variant.id
					variantId = variant.id
					cart = await addItemsToCart(
						[item],
						sections,
						optimisticStarted
							? {
									conflictMode:
										shouldResolveExclusiveConflicts
											? 'exclusive_only'
											: 'none',
								}
							: undefined
					)
				}
			}
		}

		// ── 7. Handle result ────────────────────────────────────────
		if ('status' in cart) {
			if (optimisticStarted) {
				forceReleaseAllLocks()
				if (cartDrawer) {
					void cartDrawer.forceRefreshFromServer()
				}
			}
			cartDebug('cart-api:add:error', {
				error: cart.description || cart.message || 'Unknown error',
				status: cart.status,
			})
			console.error('[CartAPI] Add to cart failed:', cart)
			return {
				success: false,
				error: cart.description || cart.message || 'Unknown error',
			}
		}

		// Fire-and-forget side effects AFTER the critical path
		refreshCartSnapshot()
		trackProductAdded(cart)

		publish(PUB_SUB_EVENTS.cartUpdate, {
			source: 'cart-api',
			productVariantId: variantId,
		})

		if (options.goToCheckout) {
			if (optimisticStarted) forceReleaseAllLocks()
			window.location.href = '/checkout'
		} else if (options.openCart !== false) {
			if (
				optimisticStarted &&
				cartDrawer &&
				lockToken &&
				'sections' in cart &&
				cart.sections
			) {
				cartDebug('cart-api:add:handle-server-response', {
					variantId,
					lockToken,
					conflictsResolved: cart._conflictsResolved,
				})
				// When exclusive conflicts are resolved, the cart state on server
				// changes but the initial /cart/add.js response sections are stale
				// (rendered before server-side update-instructions ran). Strip the
				// sections so handleServerResponse fetches fresh ones instead of
				// rendering stale HTML that still shows evicted items.
				if (cart._conflictsResolved) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					delete (cart as any).sections
				}
				// Lock-aware path: handleServerResponse manages the update cycle
				cartDrawer.handleServerResponse(cart, lockToken)
			} else {
				cartDebug('cart-api:add:render-response-directly', {
					variantId,
				})
				if (optimisticStarted) forceReleaseAllLocks()
				await renderResponseToCartDrawer(cart)
			}
		}

		// Attribution sync AFTER the update cycle — these hit Shopify's
		// session-locked cart endpoint and must not compete with the
		// background update cycle (add GWP, remove membership, etc.)
		const runAttribution = () => {
			void Promise.all([
				syncPayLaterCartAttribute(options.payLater ?? null),
				syncInternalAttribFromCookie(),
			]).catch((e) =>
				console.error('[CartAPI] Attribution sync error:', e)
			)
		}
		if (!isLocked()) {
			runAttribution()
		} else {
			const poll = setInterval(() => {
				if (!isLocked()) {
					clearInterval(poll)
					runAttribution()
				}
			}, 200)
		}

		return { success: true }
	} catch (e) {
		cartDebug('cart-api:add:exception', {
			error: e instanceof Error ? e.message : String(e),
		})
		forceReleaseAllLocks()
		const message = e instanceof Error ? e.message : 'Unknown error'
		console.error('[CartAPI] Error:', e)
		return { success: false, error: message }
	} finally {
		isProcessing = false
	}
}

export async function cartApiCheckout(
	input: number | string,
	options: CartApiOptions & { payLater: string }
): Promise<CartApiResult> {
	const shouldGoToCheckout =
		options.payLater === 'afterpay' || options.payLater === 'paypal'

	return cartApiAdd(input, {
		...options,
		goToCheckout: shouldGoToCheckout || options.goToCheckout,
		openCart: shouldGoToCheckout ? false : options.openCart,
	})
}

/**
 * Before adding to cart, clean up conflicting line items:
 * 1. When using pay-later (truemed/afterpay/paypal), remove memberships (not subscription-compatible)
 * 2. When using truemed, remove GWP items (not eligible)
 * Note: We no longer remove the same bundle variant - this is handled by the caller
 */
async function preAddCartCleanup(
	variantId: number,
	payLater: string | null,
	existingCart?: Cart | CartErrorResponse
): Promise<void> {
	const currentCart = existingCart ?? (await getCart())
	if ('status' in currentCart) return

	const updates: Record<string, number> = {}

	// Pay-later methods don't support subscriptions — remove memberships
	if (payLater) {
		for (const item of currentCart.items) {
			if (isMembershipCartItem(item)) {
				updates[item.key] = 0
			}
		}
	}

	// TrueMed is not eligible for GWP
	if (payLater === 'truemed') {
		for (const item of currentCart.items) {
			if (item.properties?._gwp_item) {
				updates[item.key] = 0
			}
		}
	}

	if (Object.keys(updates).length === 0) return

	cartDebug('cart-api:add:pre-cleanup-updates', {
		variantId,
		payLater,
		updates,
	})

	await fetch(window.routes.cart_update_url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ updates }),
	})
		.then((r) =>
			r
				.clone()
				.json()
				.catch(() => null)
				.then((body) => ({ ok: r.ok, status: r.status, body }))
		)
		.then((result) => {
			cartDebug('cart-api:add:pre-cleanup-response', result)
		})
}

function trackProductAdded(cart: { items: { [key: string]: unknown }[] }) {
	try {
		if (!window.SegmentUser) return

		for (const item of cart.items) {
			void window.SegmentUser.sendSegmentEvent(null, {
				eventName: 'Product Added',
				sent_from: 'Pvolve Web Script',
				brand: item.vendor as string,
				category: item.product_type as string,
				url: window.location.origin + (item.url as string),
				product_id: item.product_id as number,
				sku: item.sku as string,
				name: item.product_title as string,
				price: ((item.final_price as number) ?? 0) / 100,
				quantity: item.quantity as number,
				variant: (item.variant_title as string) || '',
				shopify_product_id: item.product_id as number,
				shopify_variant_id: item.id as number,
				currency: 'USD',
				image_url: item.image as string,
			})
		}
	} catch (e) {
		console.error('[CartAPI] Error tracking Product Added:', e)
	}
}

export function initCartApi() {
	initBackForwardCartRevalidation()

	const add = (input: number | string, options?: CartApiOptions) =>
		cartApiAdd(input, options)

	const addWithMembership = (
		input: number | string,
		options?: CartApiOptions
	) => cartApiAdd(input, options)

	const checkout = (
		input: number | string,
		options: CartApiOptions & { payLater: string }
	) => cartApiCheckout(input, options)

	// New API namespace
	window.pvolve = { cart: { add, addWithMembership, checkout } }

	// Legacy compat shims
	window.dispatchCartAddEvent = (
		productId: string | number,
		options?: CartApiOptions
	) => {
		return cartApiAdd(normalizeCartApiInput(productId), {
			...options,
			isProductId: true,
		})
	}

	window.addToCartWithMembership = (
		variantId: string | number,
		qty?: number,
		openCart?: boolean,
		payLater?: string,
		goToCheckout?: boolean
	) =>
		cartApiAdd(normalizeCartApiInput(variantId), {
			quantity: qty,
			openCart,
			payLater: payLater || null,
			goToCheckout,
		})

	// Alpine.store('utils').cart shim for legacy Skyline callers
	shimAlpineStore()
}

function shimAlpineStore() {
	const cartShim = {
		checkoutWithDeferredPayment: (
			variantId: number | string,
			_product: unknown,
			quantity?: number,
			provider?: string,
			purchaseAsGift?: boolean
		) => {
			const id = normalizeCartApiInput(variantId)
			return cartApiCheckout(id, {
				quantity: quantity ?? 1,
				payLater: provider || '',
				purchaseAsGift: purchaseAsGift ?? false,
			})
		},
		addProductByID: (
			variant: { id: number } | number | string,
			quantity?: number,
			sellingPlanId?: number,
			_productType?: string,
			_product?: unknown,
			openCart?: boolean,
			purchaseAsGift?: boolean
		) => {
			const id =
				typeof variant === 'object' && variant !== null
					? variant.id
					: normalizeCartApiInput(variant)
			return cartApiAdd(id, {
				quantity: quantity ?? 1,
				sellingPlanId,
				openCart: openCart ?? true,
				purchaseAsGift: purchaseAsGift ?? false,
			})
		},
	}

	const storeMap: Record<string, unknown> = {
		utils: { cart: cartShim },
	}

	if (!window.Alpine?.store) {
		window.Alpine = {
			store: (name: string) => storeMap[name],
		}
	}
}
